/**
 * Use this file for JavaScript code that you want to run in the front-end 
 * on posts/pages that contain this block.
 *
 * When this file is defined as the value of the `viewScript` property
 * in `block.json` it will be enqueued on the front end of the site.
 *
 * Example:
 *
 * ```js
 * {
 *   "viewScript": "file:./view.js"
 * }
 * ```
 *
 * If you're not making any changes to this file because your project doesn't need any 
 * JavaScript running in the front-end, then you should delete this file and remove 
 * the `viewScript` property from `block.json`. 
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-metadata/#view-script
 */

/* eslint-disable no-console */
// console.log("Hello World! (from ultime-blocks-sql-insert-update-delete[d-visualizer-fr block)");
/* eslint-enable no-console */

(function () {
    // ── Données partagées ──────────────────────────────────
    const employes = [
        { id: 1, nom: "Alice", departement: "Ventes", salaire: 2800 },
        { id: 2, nom: "Bob", departement: "Tech", salaire: 3500 },
        { id: 3, nom: "Claire", departement: "Ventes", salaire: 3100 },
        { id: 4, nom: "David", departement: "Interim", salaire: 1800 },
        { id: 5, nom: "Emma", departement: "Ventes", salaire: 2600 },
        { id: 6, nom: "Farid", departement: "Interim", salaire: 1900 },
        { id: 7, nom: "Gaëlle", departement: "Tech", salaire: 4000 },
        { id: 8, nom: "Hugo", departement: "Interim", salaire: 2100 }
    ];

    // Nouvelle ligne à insérer
    const newRow = { id: 9, nom: "Inès", departement: "Tech", salaire: 3300 };

    const COLS = ["id", "nom", "departement", "salaire"];

    // ── Définition des requêtes ────────────────────────────
    const queries = {

        // ── INSERT ────────────────────────────────────────
        insert: {
            parts: [
                { id: "i-table", text: "INSERT INTO employes (id, nom, departement, salaire)" },
                { id: "i-values", text: "VALUES (9, 'Inès', 'Tech', 3300);" }
            ],
            steps: [
                {
                    key: "i-table",
                    sql: "INSERT INTO employes — table cible",
                    desc: "Le moteur identifie la table cible <code>employes</code> et les colonnes à remplir.",
                    action: (data) => data.map(r => ({ ...r, _state: "normal" }))
                },
                {
                    key: "i-values",
                    sql: "VALUES (9, 'Inès', 'Tech', 3300)",
                    desc: "Les valeurs de la nouvelle ligne sont évaluées et préparées en mémoire.",
                    action: (data) => [
                        ...data,
                        { ...newRow, _state: "pending" }
                    ]
                },
                {
                    key: "i-check",
                    sql: "Vérification des contraintes",
                    desc: "Le moteur vérifie les contraintes : NOT NULL, UNIQUE (id=9 n'existe pas), FK, CHECK…",
                    action: (data) => data
                },
                {
                    key: "i-write",
                    sql: "Écriture en base",
                    desc: "La nouvelle ligne est insérée définitivement dans la table.",
                    action: (data) => data.map(r =>
                        r._state === "pending" ? { ...r, _state: "inserted" } : r
                    )
                }
            ]
        },

        // ── UPDATE ────────────────────────────────────────
        update: {
            parts: [
                { id: "u-table", text: "UPDATE employes" },
                { id: "u-set", text: "SET salaire = salaire * 1.10" },
                { id: "u-where", text: "WHERE departement = 'Ventes';" }
            ],
            steps: [
                {
                    key: "u-table",
                    sql: "UPDATE employes — table cible",
                    desc: "Le moteur identifie la table <code>employes</code> à modifier.",
                    action: (data) => data.map(r => ({ ...r, _state: "normal" }))
                },
                {
                    key: "u-where",
                    sql: "WHERE departement = 'Ventes'",
                    desc: "Les lignes dont <code>departement = 'Ventes'</code> sont sélectionnées pour modification.",
                    action: (data) => data.map(r => ({
                        ...r,
                        _state: r.departement === "Ventes" ? "targeted" : "normal"
                    }))
                },
                {
                    key: "u-set",
                    sql: "SET salaire = salaire * 1.10",
                    desc: "Les nouvelles valeurs sont calculées : <code>salaire × 1.10</code> pour chaque ligne ciblée.",
                    action: (data) => data.map(r => r._state === "targeted"
                        ? { ...r, salaire: Math.round(r.salaire * 1.10), _state: "modified" }
                        : r
                    )
                },
                {
                    key: "u-check",
                    sql: "Vérification des contraintes",
                    desc: "Le moteur vérifie les contraintes (NOT NULL, CHECK, FK…) sur les nouvelles valeurs.",
                    action: (data) => data
                },
                {
                    key: "u-write",
                    sql: "Écriture en base",
                    desc: "Les modifications sont validées et écrites définitivement dans la table.",
                    action: (data) => data.map(r => ({
                        ...r,
                        _state: r._state === "modified" ? "written" : "normal"
                    }))
                }
            ]
        },

        // ── DELETE ────────────────────────────────────────
        delete: {
            parts: [
                { id: "d-table", text: "DELETE FROM employes" },
                { id: "d-where", text: "WHERE departement = 'Interim';" }
            ],
            steps: [
                {
                    key: "d-table",
                    sql: "DELETE FROM employes — table cible",
                    desc: "Le moteur identifie la table <code>employes</code> dans laquelle supprimer des lignes.",
                    action: (data) => data.map(r => ({ ...r, _state: "normal" }))
                },
                {
                    key: "d-where",
                    sql: "WHERE departement = 'Interim'",
                    desc: "Les lignes dont <code>departement = 'Interim'</code> sont sélectionnées pour suppression.",
                    action: (data) => data.map(r => ({
                        ...r,
                        _state: r.departement === "Interim" ? "targeted" : "normal"
                    }))
                },
                {
                    key: "d-check",
                    sql: "Vérification des contraintes",
                    desc: "Le moteur vérifie les contraintes FK (<code>ON DELETE CASCADE / RESTRICT</code>…) avant suppression.",
                    action: (data) => data
                },
                {
                    key: "d-delete",
                    sql: "Suppression des lignes",
                    desc: "Les lignes ciblées sont supprimées. Les autres restent intactes.",
                    action: (data) => data.map(r => ({
                        ...r,
                        _state: r._state === "targeted" ? "deleted" : r._state
                    }))
                }
            ]
        }
    };

    const virtualKeys = ["i-check", "i-write", "u-check", "u-write", "d-check", "d-delete"];

    // ── État global ────────────────────────────────────────
    let activeKey = "insert";
    let activeQuery = queries.insert;
    let curStepIdx = -1;
    let currentData = employes.map(r => ({ ...r }));

    // ── Rendu de la requête SQL ────────────────────────────
    function renderSql() {
        document.getElementById("sql-display").innerHTML =
            activeQuery.parts
                .map(p => `<span class="sql-part" id="${p.id}">${p.text}</span>`)
                .join("\n");
    }

    // ── Rendu de la table mini ─────────────────────────────
    function renderMini(data) {
        const rows = data.map(r => {
            const cls =
                r._state === "written" ? "written" :
                    r._state === "inserted" ? "inserted" :
                        r._state === "modified" ? "modified" :
                            r._state === "deleted" ? "deleted" :
                                r._state === "targeted" ? "targeted" :
                                    r._state === "pending" ? "pending" : "";
            const cells = COLS.map(c => `<td>${r[c] !== undefined ? r[c] : ""}</td>`).join("");
            return `<tr class="${cls}">${cells}</tr>`;
        }).join("");

        const thead = COLS.map(c => `<th>${c}</th>`).join("");

        // Légende dynamique
        const legends = [];
        if (data.some(r => r._state === "pending")) {
            legends.push(`<span><i class="legend-dot" style="background:#fef9c3;border:1px solid #713f12;"></i> Nouvelle ligne en attente d'être insérée</span>`);
        }
        if (data.some(r => r._state === "inserted")) {
            legends.push(`<span><i class="legend-dot" style="background:#bbf7d0;border:1px solid #14532d;"></i> Ligne insérée</span>`);
        }
        if (data.some(r => r._state === "targeted")) {
            legends.push(`<span><i class="legend-dot" style="background:#fef08a;border:1px solid #713f12;"></i> Ciblé par WHERE</span>`);
        }
        if (data.some(r => r._state === "modified")) {
            legends.push(`<span><i class="legend-dot" style="background:#93c5fd;border:1px solid #1e3a5f;"></i> Modifié par SET</span>`);
        }
        if (data.some(r => r._state === "written")) {
            legends.push(`<span><i class="legend-dot" style="background:#86efac;border:1px solid #14532d;"></i> Écrit en base</span>`);
        }
        if (data.some(r => r._state === "deleted")) {
            legends.push(`<span><i class="legend-dot" style="background:#fca5a5;border:1px solid #7f1d1d;"></i> Supprimé</span>`);
        }

        const legendHtml = legends.length
            ? `<div class="legend">${legends.join("")}</div>`
            : "";

        document.getElementById("step-table").innerHTML =
            `<table class="mini">
                <thead><tr>${thead}</tr></thead>
                <tbody>${rows}</tbody>
             </table>${legendHtml}`;
    }

    // ── Mise à jour de l'UI à chaque étape ────────────────
    function updateStep(i) {
        curStepIdx = i;
        const step = activeQuery.steps[i];

        document.querySelectorAll(".sql-part").forEach(p => p.classList.remove("active"));
        if (!virtualKeys.includes(step.key)) {
            const el = document.getElementById(step.key);
            if (el) el.classList.add("active");
        }

        // Recalcul depuis le début
        let src = employes.map(r => ({ ...r }));
        for (let j = 0; j <= i; j++) {
            src = activeQuery.steps[j].action(src);
        }
        currentData = src;

        const affected = currentData.filter(r =>
            ["targeted", "modified", "written", "deleted", "pending", "inserted"].includes(r._state)
        ).length;

        document.getElementById("step-meta").innerHTML =
            `<strong>Étape ${i + 1} / ${activeQuery.steps.length} — ${step.sql}</strong><br>
             ${step.desc}
             <span class="badge-rows">${affected} ligne(s) concernée(s)</span>`;

        renderMini(currentData);
    }

    // ── Reset ──────────────────────────────────────────────
    function reset() {
        curStepIdx = -1;
        currentData = employes.map(r => ({ ...r }));
        document.querySelectorAll(".sql-part").forEach(p => p.classList.remove("active"));
        document.getElementById("step-meta").textContent =
            "Cliquez sur un bouton pour avancer ou reculer dans l'exécution.";
        document.getElementById("step-table").innerHTML = "";
    }

    // ── Navigation ─────────────────────────────────────────
    document.getElementById("btn-next").addEventListener("click", () => {
        if (curStepIdx < activeQuery.steps.length - 1) {
            updateStep(curStepIdx + 1);
        }
    });

    document.getElementById("btn-prev").addEventListener("click", () => {
        if (curStepIdx > 0) {
            updateStep(curStepIdx - 1);
        }
    });

    document.getElementById("btn-reset").addEventListener("click", reset);

    // ── Bascule onglets ────────────────────────────────────
    const tabConfig = {
        insert: { cls: "active-insert", query: queries.insert },
        update: { cls: "active-update", query: queries.update },
        delete: { cls: "active-delete", query: queries.delete }
    };

    ["insert", "update", "delete"].forEach(key => {
        document.getElementById(`tab-${key}`).addEventListener("click", () => {
            activeKey = key;
            activeQuery = tabConfig[key].query;
            // Réinitialiser les classes de tous les onglets
            ["insert", "update", "delete"].forEach(k => {
                document.getElementById(`tab-${k}`).className =
                    k === key ? `query-tab ${tabConfig[k].cls}` : "query-tab";
            });
            reset();
            renderSql();
        });
    });

    // ── Init ───────────────────────────────────────────────
    renderSql();
})()